/**
 * Axios instance pre-configured for the 5mi.uz Django backend.
 *
 * Features
 * ─────────
 * • Attaches the Bearer JWT to every outgoing request
 * • On 401 → attempts a silent token refresh (queues concurrent requests)
 * • On refresh failure → clears tokens and fires "auth:logout" event
 *   so AuthContext can navigate to the login/auth screen
 */

import axios from 'axios'

// ── Token storage ─────────────────────────────────────────────────────
const ACCESS_KEY  = '5mi_access'
const REFRESH_KEY = '5mi_refresh'

export const tokenStorage = {
  getAccess:   ()             => localStorage.getItem(ACCESS_KEY),
  getRefresh:  ()             => localStorage.getItem(REFRESH_KEY),
  setTokens:   ({ access, refresh }) => {
    localStorage.setItem(ACCESS_KEY, access)
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
  },
  clearTokens: ()             => {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

// ── Instance ─────────────────────────────────────────────────────────
// Dev: VITE_API_BASE_URL='' → Vite proxy routes /api/* to localhost:8000
// Prod: VITE_API_BASE_URL=https://api.5mi.uz
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
})

// ── Request: attach access token ─────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccess()
    if (token) config.headers.Authorization = `Bearer ${token}`
    // FormData: delete Content-Type to let browser/axios set multipart/form-data automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json'
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ── Response: silent refresh on 401 ──────────────────────────────────
let _refreshing = false
let _queue      = []   // [{ resolve, reject }]

function drainQueue(error, token = null) {
  _queue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token),
  )
  _queue = []
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config

    // Only handle 401; skip the refresh endpoint itself to avoid loops
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes('/api/auth/token/refresh/')
    ) {
      return Promise.reject(error)
    }

    // ── Queue concurrent requests while one refresh is in flight ────
    if (_refreshing) {
      return new Promise((resolve, reject) =>
        _queue.push({ resolve, reject }),
      ).then((token) => {
        original.headers.Authorization = `Bearer ${token}`
        return api(original)
      })
    }

    original._retry = true
    _refreshing     = true

    const refreshToken = tokenStorage.getRefresh()
    if (!refreshToken) {
      tokenStorage.clearTokens()
      drainQueue(error)
      _refreshing = false
      window.dispatchEvent(new CustomEvent('auth:logout'))
      return Promise.reject(error)
    }

    try {
      const { data } = await axios.post(`${BASE_URL}/api/auth/token/refresh/`, {
        refresh: refreshToken,
      })
      tokenStorage.setTokens({ access: data.access, refresh: data.refresh })
      api.defaults.headers.common.Authorization = `Bearer ${data.access}`
      drainQueue(null, data.access)
      original.headers.Authorization = `Bearer ${data.access}`
      return api(original)
    } catch (refreshError) {
      tokenStorage.clearTokens()
      drainQueue(refreshError)
      window.dispatchEvent(new CustomEvent('auth:logout'))
      return Promise.reject(refreshError)
    } finally {
      _refreshing = false
    }
  },
)

export default api

